import { useState, MouseEvent, Fragment, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import { LinkType } from "types/menu";
import { useSetRecoilState } from "recoil";
import { sidebarState } from "base/store/sidebar";
import { routeState } from "base/store/route";
import {
  MANUSCRIPT_BOOK_URL,
  MANUSCRIPT_NOVEL_URL,
  MANUSCRIPT_ALL_URL,
  MANUSCRIPT_LIST_URL,
} from "manuscript/constants/route";
import { BaseManuscriptPageParams } from "types/page";

interface Props {
  links: LinkType[] | undefined;
  level: number;
}

const MenuList = ({ links, level }: Props) => {
  const { category_type, category_id } = useParams<BaseManuscriptPageParams>();
  const navigate = useNavigate();
  const location = useLocation();
  const setSidebarData = useSetRecoilState(sidebarState);
  const setCurrentRouteData = useSetRecoilState(routeState);
  const [expandMap, setExpandMap] = useState<{
    [key: string | number]: boolean;
  }>({});

  useEffect(() => {
    if (!links) {
      return;
    }

    // console.log("path", location.pathname);
    const isMatchedList =
      location.pathname.startsWith(MANUSCRIPT_BOOK_URL) ||
      location.pathname.startsWith(MANUSCRIPT_NOVEL_URL);
    links.forEach((link, idx) => {
      // Init expandMap state
      if (link.isExpand) {
        const isMatched = location.pathname.startsWith(link.href);
        setExpandMap((prev) => ({ ...prev, [idx]: isMatched ? true : false }));
      }
      if (link.href === MANUSCRIPT_ALL_URL && isMatchedList) {
        setExpandMap((prev) => ({ ...prev, [idx]: true }));
      }

      // Init data for page title
      if (location.pathname === link.href) {
        setCurrentRouteData({ currentRouteName: link.text });
      }
    });
  }, [links]);

  const renderExpandIcon = (
    isExpand: boolean | undefined,
    index: string | number
  ) => {
    if (!isExpand) return null;

    const onClick = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setExpandMap({ ...expandMap, [index]: !expandMap[index] });
    };

    return (
      <ListItemIcon onClick={onClick}>
        {expandMap[index] ? <ExpandLess /> : <ExpandMore />}
      </ListItemIcon>
    );
  };

  const checkSelectedItem = (href: string) => {
    if (location.pathname === href) {
      return true;
    }

    if (`${MANUSCRIPT_LIST_URL}/${category_type}/${category_id}` === href) {
      return true;
    }
  };

  if (!links) {
    return <></>;
  }

  return (
    <List disablePadding>
      {links.map(
        ({ href, text, icon: Icon, cantNavigate, isExpand, subLinks }, idx) => {
          return (
            <Fragment key={href}>
              <ListItem disablePadding>
                <ListItemButton
                  sx={{ pl: level === 0 ? 2 : level * 4 }}
                  selected={checkSelectedItem(href)}
                  onClick={() => {
                    if (!cantNavigate) {
                      setCurrentRouteData({ currentRouteName: text });
                      navigate(href);
                      setSidebarData({ isShowSidebar: false });
                    }

                    if (!expandMap[idx]) {
                      setExpandMap({ ...expandMap, [idx]: true });
                    }
                  }}
                >
                  <ListItemIcon>{Icon && <Icon />}</ListItemIcon>
                  <ListItemText primary={text} />
                  {renderExpandIcon(isExpand, idx)}
                </ListItemButton>
              </ListItem>

              {subLinks && (
                <Collapse in={expandMap[idx]} timeout="auto">
                  <MenuList links={subLinks} level={level + 1} />
                </Collapse>
              )}
            </Fragment>
          );
        }
      )}
    </List>
  );
};

export default MenuList;
